import React from 'react';
import { Building2, MapPin, Plus, Trash2 } from 'lucide-react';
import { Location } from '../../types';

interface LocationFormSectionProps {
	locations: Location[];
	onLocationsChange: (locations: Location[]) => void;
	errors: Record<string, string>;
	onErrorChange: (field: string, error: string) => void;
}

export const LocationFormSection: React.FC<LocationFormSectionProps> = ({
	locations,
	onLocationsChange,
	errors,
	onErrorChange,
}) => {
	const addLocation = () => {
		const newLocation: Location = {
			id: '',
			name: '',
			gpLocationId: '',
			targetLocationId: null,
			isHeadOffice: false,
			status: 'ACTIVE',
			clientId: '',
			createdAt: '',
			updatedAt: '',
		};
		onLocationsChange([...locations, newLocation]);
	};

	const removeLocation = (index: number) => {
		if (locations.length <= 1) return;
		const updated = locations.filter((_, i) => i !== index);
		// If removed location was HO and there's only one left, auto-mark it as HO
		const hasHO = updated.some((loc) => loc.isHeadOffice);
		if (!hasHO && updated.length === 1) {
			updated[0] = { ...updated[0], isHeadOffice: true } as Location;
		}
		onLocationsChange(updated);
		onErrorChange(`location_${index}_name`, '');
		onErrorChange(`location_${index}_gpLocationId`, '');
		onErrorChange(`location_${index}_targetLocationId`, '');
	};

	const updateField = (index: number, field: keyof Location, value: string | null) => {
		const updated = [...locations];
		updated[index] = { ...updated[index], [field]: value } as Location;
		onLocationsChange(updated);
		onErrorChange(`location_${index}_${field}`, '');
	};

	const setHeadOffice = (index: number) => {
		// Radio behaviour: only one HO at a time
		const updated = locations.map((loc, i) => ({ ...loc, isHeadOffice: i === index }));
		onLocationsChange(updated);
		onErrorChange('locations_headoffice', '');
	};

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between">
				<h3 className="text-lg font-medium text-gray-900 flex items-center">
					<MapPin className="h-5 w-5 mr-2 text-blue-600" />
					Location Management
				</h3>
				<button
					type="button"
					onClick={addLocation}
					className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500">
					<Plus className="h-4 w-4 mr-1" />
					Add Location
				</button>
			</div>

			<div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
				<div className="flex items-start">
					<MapPin className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
					<div className="ml-3">
						<h4 className="text-sm font-medium text-blue-800">About Locations</h4>
						<p className="mt-1 text-sm text-blue-700">
							At least one location is required. Mark exactly one as <strong>Head Office</strong> — this is used when no
							location is provided on an incoming event. All fields are required.
						</p>
					</div>
				</div>
			</div>

			{errors.locations_headoffice && (
				<div className="bg-red-50 border border-red-200 rounded-lg p-3">
					<p className="text-red-800 text-sm">{errors.locations_headoffice}</p>
				</div>
			)}

			{locations.length === 0 && (
				<div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
					<MapPin className="mx-auto h-12 w-12 text-gray-400" />
					<h3 className="mt-2 text-sm font-medium text-gray-900">No locations</h3>
					<p className="mt-1 text-sm text-gray-500">Get started by adding your first location.</p>
					<div className="mt-6">
						<button
							type="button"
							onClick={addLocation}
							className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
							<Plus className="h-4 w-4 mr-2" />
							Add Location
						</button>
					</div>
				</div>
			)}

			<div className="space-y-4">
				{locations.map((location, index) => (
					<div
						key={index}
						className={`border rounded-lg p-4 ${location.isHeadOffice ? 'bg-amber-50 border-amber-300' : 'bg-gray-50 border-gray-200'}`}>
						<div className="flex items-center justify-between mb-4">
							<div className="flex items-center gap-3">
								<h4 className="text-sm font-medium text-gray-900">Location {index + 1}</h4>
								{location.isHeadOffice && (
									<span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-300">
										<Building2 className="h-3 w-3" />
										Head Office
									</span>
								)}
							</div>
							<div className="flex items-center gap-3">
								{!location.isHeadOffice && (
									<button
										type="button"
										onClick={() => setHeadOffice(index)}
										className="text-xs text-amber-700 hover:text-amber-900 border border-amber-300 bg-amber-50 hover:bg-amber-100 px-2 py-1 rounded focus:outline-none">
										Set as Head Office
									</button>
								)}
								{locations.length > 1 && (
									<button
										type="button"
										onClick={() => removeLocation(index)}
										className="text-red-600 hover:text-red-800 focus:outline-none"
										title="Remove location">
										<Trash2 className="h-4 w-4" />
									</button>
								)}
							</div>
						</div>

						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Location Name <span className="text-red-500">*</span>
								</label>
								<input
									type="text"
									value={location.name || ''}
									onChange={(e) => updateField(index, 'name', e.target.value)}
									className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
										errors[`location_${index}_name`] ? 'border-red-300' : 'border-gray-300'
									}`}
									placeholder="e.g., Main Branch, Downtown Store"
								/>
								{errors[`location_${index}_name`] && (
									<p className="text-red-500 text-sm mt-1">{errors[`location_${index}_name`]}</p>
								)}
							</div>

							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									GP Location ID <span className="text-red-500">*</span>
								</label>
								<input
									type="text"
									value={location.gpLocationId || ''}
									onChange={(e) => updateField(index, 'gpLocationId', e.target.value)}
									className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
										errors[`location_${index}_gpLocationId`] ? 'border-red-300' : 'border-gray-300'
									}`}
									placeholder="e.g., 47L61E3cKhWvcRb"
								/>
								{errors[`location_${index}_gpLocationId`] && (
									<p className="text-red-500 text-sm mt-1">{errors[`location_${index}_gpLocationId`]}</p>
								)}
								<p className="text-xs text-gray-500 mt-1">This ID comes from your GaragePlug system</p>
							</div>

							<div className="md:col-span-2">
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Target Location ID{' '}
									{location.isHeadOffice ? (
										<span className="text-gray-400 font-normal">(optional for Head Office)</span>
									) : (
										<span className="text-red-500">*</span>
									)}
								</label>
								<input
									type="text"
									value={location.targetLocationId || ''}
									onChange={(e) => updateField(index, 'targetLocationId', e.target.value.trim() || null)}
									className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
										errors[`location_${index}_targetLocationId`] ? 'border-red-300' : 'border-gray-300'
									}`}
									placeholder={
										location.isHeadOffice
											? 'Optional — only needed if HQ events require location routing'
											: 'External system location identifier (e.g., Odoo company_id, MSD warehouse ID)'
									}
								/>
								{errors[`location_${index}_targetLocationId`] && (
									<p className="text-red-500 text-sm mt-1">{errors[`location_${index}_targetLocationId`]}</p>
								)}
								<p className="text-xs text-gray-500 mt-1">
									{location.isHeadOffice
										? 'Leave blank if your integration does not require a target location for HQ events'
										: 'The location ID in the target system (Zoho, Odoo, MSD)'}
								</p>
							</div>
						</div>
					</div>
				))}
			</div>

			{errors.locations && (
				<div className="bg-red-50 border border-red-200 rounded-lg p-3">
					<p className="text-red-800 text-sm">{errors.locations}</p>
				</div>
			)}
		</div>
	);
};
